import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setEmailVerified(session?.user?.email_confirmed_at != null);

      if (session?.user?.id) {
        const { data: twoFactorData } = await supabase
          .from('user_2fa')
          .select('is_enabled')
          .eq('user_id', session.user.id)
          .maybeSingle();

        setTwoFactorEnabled(twoFactorData?.is_enabled || false);
      }

      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      (async () => {
        setSession(session);
        setUser(session?.user ?? null);
        setEmailVerified(session?.user?.email_confirmed_at != null);

        if (session?.user?.id) {
          const { data: twoFactorData } = await supabase
            .from('user_2fa')
            .select('is_enabled')
            .eq('user_id', session.user.id)
            .maybeSingle();

          setTwoFactorEnabled(twoFactorData?.is_enabled || false);
        } else {
          setTwoFactorEnabled(false);
        }

        setLoading(false);
      })();
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
  email: string,
  password: string,
  referralCode?: string,
  firstName?: string,
  lastName?: string,
  country?: string
) => {
  try {
    // Create the auth user with metadata
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: undefined,
        data: {
          referral_code: referralCode || null,
          first_name: firstName || null,
          last_name: lastName || null,
          country: country || null
        }
      }
    });

    if (data.user && !error) {
      try {
        // Wait a bit for trigger to complete
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Update additional user info if provided
        if (firstName || lastName || country) {
          const { error: updateError } = await supabase
            .from('users')
            .update({
              first_name: firstName || null,
              last_name: lastName || null,
              country: country || null,
            })
            .eq('id', data.user.id);

          if (updateError) {
            console.error('Error updating user info:', updateError);
          }
        }

        console.log('Successfully set up new user');
      } catch (setupError) {
        console.error('Error during user setup:', setupError);
      }
    }

    if (error || !data?.user) {
      return { data, error };
    }

    return { data, error };
  } catch (error) {
    console.error('Error during sign up:', error);
    return {
      data: { user: null, session: null },
      error: new Error('An error occurred during sign up')
    };
  }
};


  const verifyEmail = async (email: string, otp: string) => {
    // Email verification is disabled
    return { success: true, message: "Email verification is disabled" };
  };

  const resendVerificationOtp = async (email: string) => {
    // Email verification is disabled
    return { success: true, message: 'Email verification is disabled' };
  };

  const signIn = async (email: string, password: string) => {
    return await supabase.auth.signInWithPassword({ email, password });
  };

  // New function to complete sign-in with 2FA
  const completeSignInWith2FA = async (email: string, password: string, twoFactorCode: string) => {
    // Bypass 2FA verification and directly sign in
    return await supabase.auth.signInWithPassword({
      email,
      password,
    });
  };

  const signOut = async () => {
    return await supabase.auth.signOut();
  };

  const updatePassword = async (newPassword: string) => {
    return await supabase.auth.updateUser({
      password: newPassword,
    });
  };

  const resetPasswordForEmail = async (email: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-password-reset-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            email,
            redirectTo: `${window.location.origin}/reset-password`,
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to send reset email');
      }

      return { error: null };
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      return { error };
    }
  };

  const setupTwoFactor = async (userId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-2fa`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'setup',
            userId,
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        return { error: { message: data.error || 'Failed to setup 2FA' }, secret: null, qrCode: null };
      }

      return { error: null, secret: data.secret, qrCode: data.qrCode };
    } catch (error: any) {
      console.error('Error setting up 2FA:', error);
      return { error: { message: error.message }, secret: null, qrCode: null };
    }
  };

  const verifyAndEnableTwoFactor = async (userId: string, token: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-2fa`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'verify',
            userId,
            token,
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        return { error: { message: data.error || 'Failed to verify code' } };
      }

      await supabase
        .from('users')
        .update({ two_factor_required: false })
        .eq('id', userId);

      return { error: null };
    } catch (error: any) {
      console.error('Error verifying 2FA:', error);
      return { error: { message: error.message } };
    }
  };

  const check2FAStatus = async (userId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-2fa`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'status',
            userId,
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        return { error: { message: data.error }, isEnabled: false };
      }

      return { error: null, isEnabled: data.isEnabled };
    } catch (error: any) {
      console.error('Error checking 2FA status:', error);
      return { error: { message: error.message }, isEnabled: false };
    }
  };

  const disableTwoFactor = async (token: string) => {
    if (!user?.id) {
      return { error: { message: 'User not found' } };
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-2fa`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'disable',
            userId: user.id,
            token,
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        return { error: { message: data.error || 'Failed to disable 2FA' } };
      }

      setTwoFactorEnabled(false);
      return { error: null };
    } catch (error: any) {
      console.error('Error disabling 2FA:', error);
      return { error: { message: error.message } };
    }
  };

  return {
    user,
    session,
    loading,
    emailVerified,
    verifyingEmail,
    twoFactorEnabled,
    signUp,
    signIn,
    completeSignInWith2FA,
    signOut,
    updatePassword,
    resetPasswordForEmail,
    verifyEmail,
    resendVerificationOtp,
    setupTwoFactor,
    verifyAndEnableTwoFactor,
    check2FAStatus,
    disableTwoFactor,
  };
};

